import {
  hapticFeedbackImpactOccurred,
  hapticFeedbackNotificationOccurred,
  hapticFeedbackSelectionChanged,
  hideBackButton,
  isThemeParamsDark,
  onBackButtonClick,
  onMainButtonClick,
  setMainButtonParams,
  showBackButton,
  type ImpactHapticFeedbackStyle,
  type NotificationHapticFeedbackType,
  useSignal,
} from '@telegram-apps/sdk-react';
import { createContext, useContext, useEffect, useMemo, useRef, type ReactNode } from 'react';

import type { TmaBootstrap } from '@/lib/tma';

const TmaContext = createContext<TmaBootstrap | null>(null);

export function TmaProvider({ value, children }: { value: TmaBootstrap; children: ReactNode }) {
  // Тема Telegram — атрибутом на <html>: цвета текста акцента, успеха и подсказок в светлой
  // теме темнее (index.css), иначе оранжевый и зелёный на белом не читались (ревью 2026-09-24).
  const dark = useSignal(isThemeParamsDark);
  useEffect(() => {
    document.documentElement.dataset.theme = dark === false ? 'light' : 'dark';
  }, [dark]);
  return <TmaContext.Provider value={value}>{children}</TmaContext.Provider>;
}

export function useTma(): TmaBootstrap {
  const ctx = useContext(TmaContext);
  if (!ctx) throw new Error('useTma вне TmaProvider');
  return ctx;
}

function useLatest<T>(value: T) {
  const ref = useRef(value);
  ref.current = value;
  return ref;
}

/** Нативная кнопка «Назад» Telegram: показывается, пока компонент смонтирован и `visible`. */
export function useBackButton(visible: boolean, onClick: () => void): void {
  const handler = useLatest(onClick);

  useEffect(() => {
    if (!visible) {
      hideBackButton.ifAvailable();
      return;
    }
    showBackButton.ifAvailable();
    return () => {
      hideBackButton.ifAvailable();
    };
  }, [visible]);

  useEffect(() => {
    if (!visible || !onBackButtonClick.isAvailable()) return;
    const off = onBackButtonClick(() => handler.current());
    return off;
  }, [visible, handler]);
}

export interface MainButtonParams {
  text: string;
  onClick: () => void;
  visible?: boolean;
  enabled?: boolean;
  loading?: boolean;
}

/** Нативная MainButton Telegram для основного действия экрана (онбординг, оплата). */
export function useMainButton({
  text,
  onClick,
  visible = true,
  enabled = true,
  loading = false,
}: MainButtonParams): void {
  const handler = useLatest(onClick);

  useEffect(() => {
    setMainButtonParams.ifAvailable({
      text,
      isVisible: visible,
      isEnabled: enabled && !loading,
      isLoaderVisible: loading,
      // Тот же зелёный, что у кнопок внутри приложения (`--color-cta`): оранжевый акцент
      // остаётся за выбором, иначе главное действие сливается с выбранными пунктами.
      backgroundColor: '#2e7d4f',
      textColor: '#f7fbf8',
    });
  }, [text, visible, enabled, loading]);

  useEffect(() => {
    return () => {
      setMainButtonParams.ifAvailable({ isVisible: false, isLoaderVisible: false });
    };
  }, []);

  useEffect(() => {
    if (!onMainButtonClick.isAvailable()) return;
    const off = onMainButtonClick(() => {
      if (enabled && !loading) handler.current();
    });
    return off;
  }, [enabled, loading, handler]);
}

export interface Haptic {
  impact: (style?: ImpactHapticFeedbackStyle) => void;
  notify: (type?: NotificationHapticFeedbackType) => void;
  selection: () => void;
}

/** Хаптика на RSVP, чек-ине и выборе (BLUEPRINT 8). Вне Telegram — тихо ничего не делает. */
export function useHaptic(): Haptic {
  return useMemo(
    () => ({
      impact: (style = 'medium') => hapticFeedbackImpactOccurred.ifAvailable(style),
      notify: (type = 'success') => hapticFeedbackNotificationOccurred.ifAvailable(type),
      selection: () => hapticFeedbackSelectionChanged.ifAvailable(),
    }),
    [],
  );
}
